import React, { useState } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import UserDashboard from '../pages/UserDashboard';
import SendSMS from '../pages/SendSMS'; // PROMPT-042: Updated to new Send SMS page
import SendSMSNew from '../pages/SendSMSNew';
import SendSMSPro from '../pages/SendSMSPro';
import Templates from '../pages/user/dlt/Templates'; // PROMPT-093: Full template management UI
import UserTemplateAnalytics from '../pages/user/TemplateAnalytics';
import Headers from '../pages/super/dlt/Headers'; // Updated to modern design style
import Reports from '../pages/user/Reports';
import ReportsDetailed from '../pages/user/ReportsDetailed';
import ReportsSummary from '../pages/user/ReportsSummary';
import Settings from '../pages/user/Settings';
import ApiManagement from '../pages/user/ApiManagement';
import ServiceSidebar from '../components/layout/ServiceSidebar';
import ModernHeader from '../components/layout/ModernHeader';
// WhatsApp Business Pages (User)
import UserPushCampaign from '../pages/user/whatsapp/UserPushCampaign';
import UserWAReports from '../pages/user/whatsapp/UserWAReports';
import UserConversationReport from '../pages/user/whatsapp/UserConversationReport';
import UserWASettings from '../pages/user/whatsapp/UserWASettings';
import UserChatSettings from '../pages/user/whatsapp/UserChatSettings';
import UserWAAutomation from '../pages/user/whatsapp/UserWAAutomation';
import UserWATemplates from '../pages/user/whatsapp/UserWATemplates';
// Google RCS & Bulk Email (Coming Soon)
import RCSComingSoon from '../pages/rcs/RCSComingSoon';
import EmailComingSoon from '../pages/email/EmailComingSoon';
// Global Pages
import GlobalAPIManagement from '../pages/global/GlobalAPIManagement';
import GlobalSettingsPage from '../pages/global/GlobalSettingsPage';
import ContactBook from '../pages/ContactBook';

const UserLayout = () => {
  const { user, logout } = useAuth();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  // Service-based navigation with super main menus
  const serviceNavigation = [
    // Dashboard — top-level link (common for all services)
    { key: 'dashboard', type: 'link', name: 'Dashboard', href: '/', icon: 'LayoutDashboard', themeKey: 'global' },

    // MESSAGING
    {
      key: 'messaging',
      name: 'Messaging',
      icon: 'MessageSquare',
      themeKey: 'messaging',
      items: [
        { name: 'Send SMS', href: '/send-sms-new', icon: 'Send' },
        {
          name: 'DLT',
          icon: 'FileCheck',
          menuKey: 'msg-dlt',
          children: [
            { name: 'Headers', href: '/dlt/headers', icon: 'Heading' },
            { name: 'Templates', href: '/dlt/templates', icon: 'FileText' },
            { name: 'Template Analytics', href: '/dlt/template-analytics', icon: 'BarChart3' },
          ]
        },
        {
          name: 'Reports',
          icon: 'BarChart3',
          menuKey: 'msg-reports',
          children: [
            { name: 'Detailed SMS', href: '/reports/detailed', icon: 'FileText' },
            { name: 'SMS Summary', href: '/reports/summary', icon: 'BarChart3' },
          ]
        },
      ],
    },

    // WHATSAPP BUSINESS
    {
      key: 'whatsapp',
      name: 'WhatsApp Business',
      icon: 'MessageCircle',
      themeKey: 'whatsapp',
      items: [
        { name: 'Push Campaign', href: '/wa/push-campaign', icon: 'Megaphone' },
        {
          name: 'Reports',
          icon: 'BarChart3',
          menuKey: 'wa-reports',
          children: [
            { name: 'Delivery & Analytics', href: '/wa/reports', icon: 'BarChart3' },
            { name: 'Conversation Report', href: '/wa/reports/conversations', icon: 'MessageCircle' },
          ]
        },
        {
          name: 'Settings',
          icon: 'Settings',
          menuKey: 'wa-settings',
          children: [
            { name: 'Manage WABA', href: '/wa/settings', icon: 'Building2' },
            { name: 'Chat Settings', href: '/wa/chat-settings', icon: 'MessageCircle' },
          ]
        },
        { name: 'Chat Management', href: '/wa/chat', icon: 'Headphones' },
        { name: 'Automation', href: '/wa/automation', icon: 'Workflow' },
        {
          name: 'Templates',
          icon: 'FileText',
          menuKey: 'wa-templates',
          children: [
            { name: 'All Templates', href: '/wa/templates', icon: 'List' },
            { name: 'Template Builder', href: '/wa/templates#builder', icon: 'Wand2' },
          ]
        },
      ],
    },

    // GOOGLE RCS
    {
      key: 'rcs',
      name: 'Google RCS',
      icon: 'Smartphone',
      themeKey: 'rcs',
      comingSoon: true,
      items: [
        { name: 'RCS Messaging', href: '/rcs/coming-soon', icon: 'Smartphone' },
      ],
    },

    // BULK EMAIL
    {
      key: 'email',
      name: 'Bulk Email',
      icon: 'Mail',
      themeKey: 'email',
      comingSoon: true,
      items: [
        { name: 'Email Campaigns', href: '/email/coming-soon', icon: 'Mail' },
      ],
    },

    // CONTACT BOOK
    { key: 'contact-book', type: 'link', name: 'Contact Book', href: '/contact-book', icon: 'BookUser', themeKey: 'global' },

    // API MANAGEMENT
    { key: 'api-management', type: 'link', name: 'API Management', href: '/global/api-management', icon: 'Code2', themeKey: 'global' },

    // SETTINGS
    { key: 'global-settings', type: 'link', name: 'Settings', href: '/global/settings', icon: 'Settings', themeKey: 'global' },
  ];

  return (
    <div className="h-screen flex flex-col overflow-hidden" style={{ background: '#f8f9fb' }}>
      {/* Global Design System */}
      <style>{`
        :root {
          --w2c-primary: #4f46e5;
          --w2c-primary-light: rgba(99, 102, 241, 0.06);
          --w2c-accent: #06b6d4;
          --w2c-gradient: linear-gradient(135deg, #4f46e5 0%, #06b6d4 100%);
          --w2c-gradient-soft: linear-gradient(135deg, rgba(99, 102, 241, 0.06) 0%, rgba(6, 182, 212, 0.04) 100%);
          --w2c-border: rgba(99, 102, 241, 0.06);
          --w2c-shadow: 0 1px 2px rgba(99, 102, 241, 0.04), 0 2px 8px rgba(99, 102, 241, 0.03);
          --w2c-shadow-hover: 0 4px 12px rgba(99, 102, 241, 0.08), 0 8px 24px rgba(99, 102, 241, 0.04);
          --w2c-radius: 10px;
          --w2c-radius-lg: 14px;
        }
        ::-webkit-scrollbar { width: 5px; height: 5px; }
        ::-webkit-scrollbar-track { background: transparent; }
        ::-webkit-scrollbar-thumb { background: rgba(99, 102, 241, 0.12); border-radius: 10px; }
        ::-webkit-scrollbar-thumb:hover { background: rgba(99, 102, 241, 0.2); }
      `}</style>
      {/* Fixed Header */}
      <ModernHeader
        setSidebarOpen={setSidebarOpen}
        user={user}
        logout={logout}
      />

      {/* Sidebar + Content Row */}
      <div className="flex flex-1 overflow-hidden" style={{ paddingTop: '62px' }}>
        {/* Service Sidebar */}
        <ServiceSidebar
          serviceNavigation={serviceNavigation}
          sidebarOpen={sidebarOpen}
          setSidebarOpen={setSidebarOpen}
          user={user}
          logout={logout}
        />

        {/* Scrollable Content Area */}
        <main className="flex-1 overflow-y-auto" style={{ paddingLeft: '0' }}>
          <div className="lg:ml-[256px] p-5 lg:p-6 max-w-full mx-auto">
            <Routes>
            <Route path="/" element={<UserDashboard />} />
            <Route path="/send-sms" element={<SendSMS />} />
            <Route path="/send-sms-new" element={<SendSMSNew />} />
            <Route path="/send-sms-pro" element={<SendSMSPro />} />
            {/* PROMPT-046-FIX-01: DLT menu routes */}
            <Route path="/dlt/headers" element={<Headers />} />
            {/* PROMPT-093: Full template management UI for users */}
            <Route path="/dlt/templates" element={<Templates />} />
            {/* PROMPT-166: Template Analytics Dashboard */}
            <Route path="/dlt/template-analytics" element={<UserTemplateAnalytics />} />
            {/* PROMPT-083: Separate report pages */}
            <Route path="/reports/detailed" element={<ReportsDetailed />} />
            <Route path="/reports/summary" element={<ReportsSummary />} />
            <Route path="/api-management" element={<ApiManagement />} />
            <Route path="/settings" element={<Settings />} />
            {/* WhatsApp Business User Routes */}
            <Route path="/wa/push-campaign" element={<UserPushCampaign />} />
            <Route path="/wa/reports" element={<UserWAReports />} />
            <Route path="/wa/reports/conversations" element={<UserConversationReport />} />
            <Route path="/wa/settings" element={<UserWASettings />} />
            <Route path="/wa/chat-settings" element={<UserWASettings />} />
            <Route path="/wa/chat" element={<UserChatSettings />} />
            <Route path="/wa/automation" element={<UserWAAutomation />} />
            <Route path="/wa/templates" element={<UserWATemplates />} />
            {/* Google RCS & Bulk Email (Coming Soon) */}
            <Route path="/rcs/coming-soon" element={<RCSComingSoon />} />
            <Route path="/email/coming-soon" element={<EmailComingSoon />} />
            {/* Contact Book */}
            <Route path="/contact-book" element={<ContactBook />} />
            {/* Global Pages */}
            <Route path="/global/api-management" element={<GlobalAPIManagement />} />
            <Route path="/global/settings" element={<GlobalSettingsPage />} />
            <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </div>
        </main>
      </div>
    </div>
  );
};

export default UserLayout;
